import React, { useState, useRef, useCallback } from 'react';
import './AudioRecorder.css';

/* ------------------------------------------------------------------ */
/*  State machine                                                      */
/* ------------------------------------------------------------------ */
const S = {
  IDLE: 'idle',
  RECORDING: 'recording',
  UPLOADING: 'uploading',
  RECORDED: 'recorded',
  PLAYING: 'playing',
  CONFIRMING: 'confirming',
  CONFIRMED: 'confirmed',
};

/* ------------------------------------------------------------------ */
/*  Helper – resolve the API / WS base depending on environment        */
/* ------------------------------------------------------------------ */
function apiBase() {
  // In production the backend is on the same origin.
  // In CRA dev the proxy handles /api, but WebSocket needs the real host.
  return process.env.REACT_APP_API_URL || '';
}
function wsBase() {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = process.env.REACT_APP_WS_HOST || window.location.host;
  return `${proto}//${host}`;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function AudioRecorder() {
  const [state, setState] = useState(S.IDLE);
  const [recordingId, setRecordingId] = useState(null);
  const [error, setError] = useState(null);
  const [duration, setDuration] = useState(0);

  const mediaRecorderRef = useRef(null);
  const wsRef = useRef(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  /* ---- helpers -------------------------------------------------- */
  const cleanup = useCallback(() => {
    clearInterval(timerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }
  }, []);

  const fmt = (s) => {
    const m = String(Math.floor(s / 60)).padStart(2, '0');
    const sec = String(s % 60).padStart(2, '0');
    return `${m}:${sec}`;
  };

  /* ---- actions -------------------------------------------------- */
  const startRecording = async () => {
    try {
      setError(null);

      // Guard: microphone API requires a Secure Context (HTTPS or localhost)
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(
          'Microphone access requires HTTPS. ' +
          'Please access this app over HTTPS or localhost.'
        );
      }

      // 1. Get a recording ID from the backend (SHA-256 of current datetime)
      const res = await fetch(`${apiBase()}/api/recording/start`, { method: 'POST' });
      if (!res.ok) throw new Error('Failed to start recording session');
      const { recordingId: id } = await res.json();
      setRecordingId(id);

      // 2. Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // 3. Open WebSocket to stream audio chunks to the backend
      const ws = new WebSocket(`${wsBase()}/ws`);
      wsRef.current = ws;

      ws.onopen = () => {
        // Tell the server which recording this is
        ws.send(JSON.stringify({ type: 'start', recordingId: id }));

        // Choose a supported MIME type
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm';

        const recorder = new MediaRecorder(stream, { mimeType });
        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0 && ws.readyState === WebSocket.OPEN) {
            ws.send(e.data); // stream binary chunk
          }
        };

        recorder.start(250); // emit a chunk every 250 ms
        setState(S.RECORDING);

        // Duration timer
        setDuration(0);
        timerRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
      };

      ws.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        if (msg.type === 'upload-complete') {
          setState(S.RECORDED);
        } else if (msg.type === 'error') {
          setError(msg.message);
          setState(S.IDLE);
        }
      };

      ws.onerror = () => {
        setError('WebSocket connection failed');
        cleanup();
        setState(S.IDLE);
      };
    } catch (err) {
      setError(err.message);
      cleanup();
      setState(S.IDLE);
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'stop' }));
    }
    setState(S.UPLOADING);
  };

  const playRecording = async () => {
    try {
      const res = await fetch(`${apiBase()}/api/recording/${recordingId}/playback`);
      if (!res.ok) throw new Error('Failed to get playback URL');
      const { url } = await res.json();

      const audio = audioRef.current;
      audio.src = url;
      audio.play();
      setState(S.PLAYING);
      audio.onended = () => setState(S.RECORDED);
    } catch (err) {
      setError(err.message);
    }
  };

  const stopPlayback = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setState(S.RECORDED);
  };

  const confirmRecording = async () => {
    try {
      setState(S.CONFIRMING);
      const res = await fetch(`${apiBase()}/api/recording/${recordingId}/confirm`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Failed to confirm recording');
      setState(S.CONFIRMED);
    } catch (err) {
      setError(err.message);
      setState(S.RECORDED);
    }
  };

  const reRecord = async () => {
    try {
      await fetch(`${apiBase()}/api/recording/${recordingId}`, { method: 'DELETE' });
    } catch {
      /* ignore */
    }
    setRecordingId(null);
    setDuration(0);
    setState(S.IDLE);
  };

  const newRecording = () => {
    setRecordingId(null);
    setDuration(0);
    setError(null);
    setState(S.IDLE);
  };

  /* ---- render --------------------------------------------------- */
  const isSecure = window.isSecureContext;

  return (
    <div className="recorder-card">
      <h1 className="recorder-title">Audio Recorder</h1>

      {/* hidden <audio> element for playback */}
      <audio ref={audioRef}>
        <track kind="captions" />
      </audio>

      {!isSecure && (
        <div className="recorder-warning">
          ⚠️ Microphone requires HTTPS. Use Chrome with:
          <code style={{ display: 'block', marginTop: '0.4rem', fontSize: '0.7rem', wordBreak: 'break-all' }}>
            --unsafely-treat-insecure-origin-as-secure={window.location.origin}
          </code>
        </div>
      )}

      {error && <div className="recorder-error">{error}</div>}

      {recordingId && (
        <p className="recorder-id">
          ID:&nbsp;<code>{recordingId.substring(0, 16)}…</code>
        </p>
      )}

      {state === S.RECORDING && (
        <div className="recorder-timer">
          <span className="pulse" />
          {fmt(duration)}
        </div>
      )}

      <div className="recorder-controls">
        {/* IDLE */}
        {state === S.IDLE && (
          <button className="btn btn-record" onClick={startRecording}>
            <span className="btn-icon">🎤</span> Record
          </button>
        )}

        {/* RECORDING */}
        {state === S.RECORDING && (
          <button className="btn btn-stop" onClick={stopRecording}>
            <span className="btn-icon">⏹</span> Stop
          </button>
        )}

        {/* UPLOADING */}
        {state === S.UPLOADING && <p className="recorder-status">Uploading…</p>}

        {/* RECORDED */}
        {state === S.RECORDED && (
          <>
            <button className="btn btn-play" onClick={playRecording}>
              <span className="btn-icon">▶</span> Play
            </button>
            <button className="btn btn-confirm" onClick={confirmRecording}>
              <span className="btn-icon">✓</span> Confirm
            </button>
            <button className="btn btn-rerecord" onClick={reRecord}>
              <span className="btn-icon">↺</span> Re-record
            </button>
          </>
        )}

        {/* PLAYING */}
        {state === S.PLAYING && (
          <>
            <button className="btn btn-stop" onClick={stopPlayback}>
              <span className="btn-icon">⏹</span> Stop
            </button>
            <button className="btn btn-confirm" onClick={confirmRecording}>
              <span className="btn-icon">✓</span> Confirm
            </button>
            <button className="btn btn-rerecord" onClick={reRecord}>
              <span className="btn-icon">↺</span> Re-record
            </button>
          </>
        )}

        {/* CONFIRMING */}
        {state === S.CONFIRMING && <p className="recorder-status">Saving…</p>}

        {/* CONFIRMED */}
        {state === S.CONFIRMED && (
          <div className="recorder-confirmed">
            <p className="recorder-success">✅ Recording saved successfully!</p>
            <button className="btn btn-record" onClick={newRecording}>
              <span className="btn-icon">🎤</span> New Recording
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
